import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderNo = params.get("order_no");
  const qrToken = params.get("t");
  const [status, setStatus] = useState(result === "cancel" ? "cancelled" : sessionId ? "checking" : "success");
  const [order, setOrder] = useState(orderNo);
  const [qrImg, setQrImg] = useState(null);

  useEffect(() => {
    if (result !== "success" || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setStatus("success"); setOrder(r.data.order_no); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep trying */ }
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [result, sessionId]);

  useEffect(() => {
    if (qrToken && status === "success") {
      api.get("/qr", { params: { data: `${window.location.origin}/invoice/${qrToken}` } })
        .then((r) => setQrImg(r.data.image)).catch(() => {});
    }
  }, [qrToken, status]);

  const config = {
    checking: { icon: Loader2, color: "text-olive", spin: true, title: "جارِ تأكيد الدفع...", desc: "لحظات من فضلك." },
    success: { icon: CheckCircle2, color: "text-olive", title: "تم استلام طلبك بنجاح!", desc: "شكراً لك. سنبدأ بتجهيز طلبك من أرضنا إلى مائدتك." },
    cancelled: { icon: XCircle, color: "text-palred", title: "أُلغيت عملية الدفع", desc: "لم يتم خصم أي مبلغ. سلتك ما زالت محفوظة." },
    failed: { icon: XCircle, color: "text-palred", title: "تعذّر إتمام الدفع", desc: "حدث خطأ أثناء الدفع، يرجى المحاولة مجدداً." },
    timeout: { icon: Loader2, color: "text-earth", title: "يتم التحقق من الدفع", desc: "إن تم الخصم فسيصلك تأكيد قريباً." },
  }[status];

  const Icon = config.icon;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 px-6 pt-24 text-center">
      <Icon className={`h-20 w-20 ${config.color} ${config.spin ? "animate-spin" : ""}`} />
      <h1 data-testid="payment-result-title" className="font-display text-3xl font-bold text-ink">{config.title}</h1>
      <p className="max-w-md text-ink/60">{config.desc}</p>
      {order && status === "success" && (
        <div className="rounded-xl bg-olive/5 px-6 py-3 font-display font-semibold text-olive-dark">رقم الطلب: {order}</div>
      )}
      {qrImg && status === "success" && (
        <div data-testid="order-qr-success" className="rounded-2xl border border-olive/10 bg-white p-5">
          <img src={qrImg} alt="QR الطلب" className="mx-auto h-36 w-36" />
          <p className="mt-3 max-w-xs text-xs leading-relaxed text-ink/55">
            احتفظ بهذا الرمز — امسحه من صفحة «حسابي ← مسح QR» بعد استلام طردك لعرض فاتورتك الإلكترونية.
          </p>
          <Link to={`/invoice/${qrToken}`} className="mt-2 inline-block text-sm font-medium text-olive link-underline">عرض الفاتورة الآن</Link>
        </div>
      )}
      <div className="flex gap-3">
        <Link to="/store" className="rounded-full bg-olive px-7 py-3 font-semibold text-ivory">متابعة التسوّق</Link>
        <Link to="/" className="rounded-full border border-olive/20 px-7 py-3 font-semibold text-olive">الرئيسية</Link>
      </div>
    </div>
  );
}
